import type { CheckboxProps } from '@chakra-ui/react'
import { Checkbox } from '@chakra-ui/react'
import React from 'react'
import type { FormLabelProps } from '../FormLabel'
import { FormLabel } from '../FormLabel'
import { withFormControl, type FormFieldProps } from './hoc/withFormControl'

// Component-specific props
export interface CheckboxFormControlComponentProps {
  variant?: CheckboxProps['variant']
  defaultChecked?: boolean
  children?: React.ReactNode
  gap?: CheckboxProps['spacing']
  checkboxLabel?: React.ReactNode
  checkboxLabelProps?: FormLabelProps
}

// Extended props that include HOC props
interface CheckboxFormControlBaseProps extends CheckboxFormControlComponentProps, FormFieldProps<boolean, HTMLInputElement> {
  // Props from HOC that we need to handle
  label?: React.ReactNode
  labelProps?: FormLabelProps
}

// Base component that handles checkbox logic
export const CheckboxFormControlBase = React.forwardRef<
  HTMLInputElement,
  CheckboxFormControlBaseProps
>((props, ref) => {
  const {
    // Form field props
    value,
    onChange,
    onBlur,
    isDisabled,
    name,
    // Component-specific props
    variant,
    defaultChecked,
    children,
    gap,
    checkboxLabel,
    checkboxLabelProps,
    // HOC props
    label,
    labelProps
  } = props

  const checked = (value === undefined || value === null) ? defaultChecked : value == true

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onChange(e.target.checked)
  }

  // Use HOC label if provided, otherwise fall back to checkboxLabel
  const labelToRender = label || checkboxLabel
  const labelPropsToUse = label ? labelProps : checkboxLabelProps

  return (
    <Checkbox
      ref={ref}
      name={name}
      isChecked={checked}
      onBlur={onBlur}
      onChange={handleChange}
      isDisabled={isDisabled}
      variant={variant}
      value={checked ? '1' : ''}
      spacing={gap ?? 3}
    >
      {labelToRender && (
        <FormLabel fontWeight={checked ? 'semibold' : 'medium'} fontSize="md" {...labelPropsToUse}>
          {labelPropsToUse?.children ?? labelToRender}
        </FormLabel>
      )}
      {children}
    </Checkbox>
  )
})

;(CheckboxFormControlBase as any).displayName = 'CheckboxFormControlBase'

// Custom withFormControl for Checkbox that doesn't render label in FormControl
const withCheckboxFormControl = <TComponentProps extends object>(
  Component: React.ComponentType<TComponentProps & FormFieldProps<boolean, HTMLInputElement>> | React.ForwardRefExoticComponent<Omit<TComponentProps & FormFieldProps<boolean, HTMLInputElement>, 'ref'> & React.RefAttributes<HTMLInputElement>>
) => {
  return withFormControl<TComponentProps, boolean, HTMLInputElement>(Component, {
    renderLabel: false // Pass label to component instead of rendering in FormControl
  })
}

// Export the wrapped component
export const CheckboxFormControl = withCheckboxFormControl<CheckboxFormControlComponentProps>(CheckboxFormControlBase)